import { useEffect, useRef, useState } from "react";

function isEmptyKpiValue(value) {
  return value === "—" || value === "" || value == null;
}

/**
 * Smooth KPI updates during navigation/refetch.
 * Holds the previous value through brief empty/intermediate states
 * so counts don't flicker (e.g. 5 → 0 → 1).
 */
export function useStableKpiValue(value, delayMs = 180) {
  const [display, setDisplay] = useState(value);
  const pendingRef = useRef(value);
  const timerRef = useRef(null);

  useEffect(() => {
    pendingRef.current = value;
    if (timerRef.current) clearTimeout(timerRef.current);

    const nextIsEmpty = isEmptyKpiValue(value);
    const displayIsEmpty = isEmptyKpiValue(display);

    const apply = () => {
      if (!Object.is(pendingRef.current, display)) {
        setDisplay(pendingRef.current);
      }
    };

    // First real value after placeholder — show immediately
    if (displayIsEmpty && !nextIsEmpty) {
      setDisplay(value);
      return undefined;
    }

    // Brief empty/refetch — keep previous number, update only if empty persists
    if (!displayIsEmpty && nextIsEmpty) {
      timerRef.current = setTimeout(apply, delayMs);
      return () => clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(apply, delayMs);
    return () => clearTimeout(timerRef.current);
  }, [value, display, delayMs]);

  return display;
}
